import { type ReactNode, useMemo, useState } from "react"
import { IconCalendarTime, IconLoader2, IconRefresh } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAppDispatch, useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import { bookingSeasonApi, type BookingSeasonSnapshot, useGetBookingSeasonQuery } from "./bookingSeasonApi"
import { AppointmentWindowCard } from "./components/AppointmentWindowCard"
import { ClosedMessagesCard } from "./components/SeasonForm"
import { SeasonSaveBar } from "./components/SeasonSaveBar"
import { SeasonStatusCard } from "./components/SeasonStatusCard"
import { formFromSnapshot, sameForm, type SeasonFormState, validateSeasonForm } from "./lib/seasonForm"

export default function BookingSeasonPage() {
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.permissions)
  // Only people who can change the season open this page — Scheduling Manager, not Appointment
  // Viewer. Viewers still see the season where it affects them, in the calendar's banner.
  const canManage = hasPermission(permissions, "appointments:manage-booking-season")

  const { data: season, isLoading, isFetching, error, refetch } = useGetBookingSeasonQuery(undefined, { skip: !canManage })

  // The admin's unsaved edits, or null while the form simply shows the current season. Keeping only
  // the edits means a refetch (another admin's save, a refresh) flows into an untouched form but never
  // overwrites one being edited.
  const [draft, setDraft] = useState<SeasonFormState | null>(null)
  const saved = useMemo(() => (season ? formFromSnapshot(season) : null), [season])
  const form = draft ?? saved

  // The three cards edit one draft and share one save. Field issues stay hidden until the first save
  // attempt, so a fresh form isn't covered in red; the server's refusal clears on the next edit.
  const [attempted, setAttempted] = useState(false)
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const shownIssues = attempted && form ? validateSeasonForm(form) : []

  function update(patch: Partial<SeasonFormState>) {
    if (!form || !saved) return
    setServerErrors([])
    const next = { ...form, ...patch }
    setDraft(sameForm(next, saved) ? null : next)
  }

  // Show the season a save or close-now answered with straight away, rather than the old one until the refetch lands.
  function followSeason(next: BookingSeasonSnapshot) {
    dispatch(bookingSeasonApi.util.upsertQueryData("getBookingSeason", undefined, next))
    setDraft(null)
  }

  if (!canManage) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Header />
        <p className="text-muted-foreground">You do not have permission to view the booking season.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <Header>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          {isFetching ? <IconLoader2 className="size-4 animate-spin" /> : <IconRefresh className="size-4" />}
          Refresh
        </Button>
      </Header>

      <p className="text-muted-foreground max-w-3xl">
        When the public website and phone line can book new appointments, and which appointment times they can book.
        Outside the booking window they are turned away with the closed messages below. Managing an existing
        appointment always works, and staff can always book, at any time. All times are Pacific.
      </p>

      {isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : error || !season || !form || !saved ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
          <p className="text-sm text-destructive">
            {(error as { status?: number } | undefined)?.status === 403
              ? "You do not have permission to view the booking season."
              : "Could not load the booking season."}
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <SeasonStatusCard season={season} onClosed={followSeason} form={form} issues={shownIssues} onChange={update} />

          <AppointmentWindowCard form={form} issues={shownIssues} onChange={update} />

          <ClosedMessagesCard form={form} issues={shownIssues} onChange={update} />

          <SeasonSaveBar
            form={form}
            unchanged={draft === null}
            attempted={attempted}
            onAttemptedChange={setAttempted}
            serverErrors={serverErrors}
            onServerErrors={setServerErrors}
            onReset={() => setDraft(null)}
            onSaved={followSeason}
          />
        </>
      )}
    </div>
  )
}

function Header({ children }: { children?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <IconCalendarTime className="size-6" />
        <h1 className="text-2xl font-bold">Booking Season</h1>
      </div>
      {children}
    </div>
  )
}
